import { runtime } from "oazapfts/lib/runtime";
import { query, explode } from "oazapfts/lib/runtime/query";
import type {
  RequestOpts,
  CreateCustomFetchOpts,
  LocalFetchRejectedResponse,
  LocalFetchResolvedResponse,
  LocalFetchResponse,
  MutationFetchRejectedResponse,
  MutationFetchResolvedResponse,
  MutationFetchResponse,
  REJECTED_STATUS,
  DetailedValidationErrorResponse,
  HandleLocalResponseOptions,
} from "../types";
import { defaults } from "../connectables/fetch/setup";

export class ApiRequestOpts implements RequestOpts {
  /** Override the base url for the API. */
  public baseUrl?: string;
  /** Custom Fetch handler **/
  public fetch?: typeof fetch;
  /** Custom way of creating a FormData object */
  public formDataConstructor?: new () => FormData;
  /** A Headers object, an object literal, or an array of two-item arrays to set request's headers. */
  public headers?: Record<string, string | number | boolean | undefined>;
  /** A string indicating how the request will interact with the browser's cache to set request's cache. */
  public cache?: RequestCache;
  /** A string indicating whether credentials will be sent with the request always, never, or only when sent to a same-origin URL. Sets request's credentials. */
  public credentials?: RequestCredentials;
  /** A cryptographic hash of the resource to be fetched by request. Sets request's integrity. */
  public integrity?: string;
  /** A boolean to set request's keepalive. */
  public keepalive?: boolean;
  /** A string to set request's method. */
  public method?: string;
  /** A string to indicate whether the request will use CORS, or will be restricted to same-origin URLs. Sets request's mode. */
  public mode?: RequestMode;
  /** A string indicating whether request follows redirects, results in an error upon encountering a redirect, or returns the redirect (in an opaque fashion). Sets request's redirect. */
  public redirect?: RequestRedirect;
  /** A string whose value is a same-origin URL, "about:client", or the empty string, to set request's referrer. */
  public referrer?: string;
  /** A referrer policy to set request's referrerPolicy. */
  public referrerPolicy?: ReferrerPolicy;
  /** An AbortSignal to set request's signal. */
  public signal?: AbortSignal | null;
  /** Can only be null. Used to disassociate request from any Window. */
  public window?: null;

  constructor(opts: RequestOpts) {
    Object.assign(this, opts);
  }
}

export function apiRequestOpts(opts: RequestOpts): ApiRequestOpts {
  return new ApiRequestOpts(opts);
}

function isLocalFetchRejectedResponse<T_Err extends PossibleErrorResponses>(
  response: unknown,
): response is LocalFetchRejectedResponse<T_Err> | MutationFetchRejectedResponse<T_Err> {
  return (
    typeof response === "object" &&
    response !== null &&
    "status" in response &&
    typeof response.status === "number" &&
    ("data" in response || "errors" in response) &&
    (response.status >= 400 || response.status < 100)
  );
}

/**
 * Try to extract the error message from a local fetch response.
 * Possible wrappers include:
 * - LocalFetchRejectedResponse | MutationFetchRejectedResponse
 * - LocalFetchError
 * - PossibleErrorResponses
 *    - ErrorResponse | Error, e.g  { message: string   }
 *    - DetailedValidationErrorResponse e.g { title: string }
 *    - string
 * @param response
 * @returns
 */
export function extractLocalFetchErrorMessage<T_Err extends PossibleErrorResponses>(
  response: LocalFetchRejectedResponse<T_Err> | MutationFetchRejectedResponse<T_Err> | LocalFetchError<T_Err>,
): string | undefined {
  // First Unwrap the response so we get to LocalFetchError
  const localFetchError = isLocalFetchRejectedResponse(response)
    ? (response.data ?? ("errors" in response ? response.errors : undefined))
    : response;

  if (!localFetchError) return;

  // Now Unwrap LocalFetchError so we get to PossibleErrorResponses
  const innerError =
    typeof localFetchError === "object" && "body" in localFetchError && !$ck._.isNil(localFetchError.body)
      ? localFetchError.body
      : localFetchError;

  if (!innerError) return;

  // extract the string message
  const errorMessage =
    typeof innerError === "object"
      ? "message" in innerError && innerError.message
        ? innerError.message
        : "title" in innerError && innerError.title
          ? innerError.title
          : innerError
      : innerError;

  if (!errorMessage || typeof errorMessage !== "string") return;

  return errorMessage;
}

export function isLocalResolvedResponse<T, T_Err extends PossibleErrorResponses>(
  response: LocalFetchResponse<T, T_Err>,
): response is LocalFetchResolvedResponse<T>;
export function isLocalResolvedResponse<T, T_Err extends PossibleErrorResponses>(
  response: MutationFetchResponse<T, T_Err>,
): response is MutationFetchResolvedResponse<T>;
export function isLocalResolvedResponse<T, T_Err extends PossibleErrorResponses>(
  response: LocalFetchResponse<T, T_Err> | MutationFetchResponse<T, T_Err>,
): response is LocalFetchResolvedResponse<T> | MutationFetchResolvedResponse<T>;
export function isLocalResolvedResponse<T, T_Err extends PossibleErrorResponses>(
  response: LocalFetchResponse<T, T_Err> | MutationFetchResponse<T, T_Err>,
): response is LocalFetchResolvedResponse<T> | MutationFetchResolvedResponse<T> {
  return response.status < 400 && response.status >= 100;
}

function parseLocalResponseError<T_Err extends PossibleErrorResponses>(
  response: LocalFetchRejectedResponse<T_Err> | MutationFetchRejectedResponse<T_Err>,
): $ck.utils.UserFriendlyError | Error {
  const errorMessage = extractLocalFetchErrorMessage(response);

  throw errorMessage?.length
    ? new $ck.utils.UserFriendlyError(errorMessage, { cause: response })
    : new Error(errorMessage, { cause: response });
}

/**
 * Extract resolved local response data and blob, bypassing 404 errors if specified.
 * If the response is not successful, it will throw an error.
 * For blob disable404Bypass does not makes true.
 * @throws {PossibleErrorResponses}
 */
export function handleLocalResponse<
  T,
  T_Err extends PossibleErrorResponses,
  O extends HandleLocalResponseOptions = HandleLocalResponseOptions,
>(response: LocalFetchResponse<T, T_Err> | MutationFetchResponse<T, T_Err>, options?: O) {
  if (!options?.disable404Bypass && response.status === 404) {
    return {
      data: undefined,
      blob: undefined,
    };
  }

  if (isLocalResolvedResponse(response)) {
    return {
      data: response.data,
      blob: "blob" in response ? response.blob : undefined,
    };
  }

  throw parseLocalResponseError(response);
}

/**
 * Parse 'workGroups[1].Name': errors... to { rowIndex, property, listName, errors  }
 * @param path a server path to indicate the row index, list name and property for a given error
 */
export function parseResponseErrorRecordPath(path: string, errors: string[]) {
  if (!errors.length) return;

  const openBracket = path.indexOf("[");
  const closeBracket = path.indexOf("]");
  if (openBracket < 0 || closeBracket < 0) return;

  const dotIndex = path.indexOf(".", closeBracket);
  const listName = path.substring(0, openBracket);
  const indexOrId = parseInt(path.substring(openBracket + 1, closeBracket));
  const propertyName = dotIndex >= 0 ? $ck._.camelCase(path.substring(dotIndex + 1)) : undefined;

  return {
    listName,
    indexOrId,
    propertyName,
    errors,
  };
}

export function isDetailedValidationErrorResponseError(
  response: LocalFetchError<PossibleErrorResponses>,
): response is LocalFetchError<DetailedValidationErrorResponse> {
  return (
    "body" in response &&
    !!response.body &&
    typeof response.body === "object" &&
    "errors" in response.body &&
    $ck._.isObject(response.body.errors)
  );
}

export function parseResponseErrorRecord<T_Err extends PossibleErrorResponses>(response: LocalFetchError<T_Err>) {
  const errorRecord = isDetailedValidationErrorResponseError(response)
    ? (response.body.errors as Record<string, string[]>)
    : undefined;

  if (!errorRecord) return;

  return $ck._.compact(Object.entries(errorRecord).map(([path, errors]) => parseResponseErrorRecordPath(path, errors)));
}

export enum FETCH_STATE {
  INITIAL = "INITIAL",
  PENDING = "PENDING",
  RESOLVED = "RESOLVED",
  REJECTED = "REJECTED",
}

export class LocalFetchError<T_ErrorType extends PossibleErrorResponses> extends Error {
  constructor(
    public type: "json" | "text" | "error",
    public body: T_ErrorType,
    public status: REJECTED_STATUS,
    cause?: Error,
  ) {
    super(JSON.stringify(body), { cause });
  }
}

export const PLANT_ID_HEADER = "Nexcor-Plant";
export const PLANT_ID_HEADER_FOR_DEFAULTS = -1000;

export function customFetchAcceptHeaderOpts(mimeType: string): CreateCustomFetchOpts {
  return {
    async beforeRequest(request) {
      request.headers.append("accept", mimeType);
    },
  };
}

export function customFetchOptsForPlantIdDefaults(): CreateCustomFetchOpts {
  return {
    async beforeRequest(request) {
      // Use plant ID -1 to get default skills not linked to a facility
      request.headers.set(PLANT_ID_HEADER, PLANT_ID_HEADER_FOR_DEFAULTS.toString());
    },
  };
}

export function makeOzapftsRuntime(override: Partial<typeof defaults> = {}) {
  return runtime({
    ...defaults,
    ...override,
  });
}

export { query as oazapftsQuery, explode as oazapftsExplode };
